import React from 'react';
import { useHistory } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { FiTrendingUp, FiGlobe, FiUsers, FiSliders, FiExternalLink, FiAlertCircle, FiArrowRight, FiBarChart2, FiFileText, FiClock, FiMail, FiMessageCircle } from 'react-icons/fi';
import { WIDGETS } from '../clientSettings/widgets/catalog';
import { CONTENT_SECTIONS, NEW_MESSAGES } from './hubData';
import { HubHeader, StatCard, hubPath, useHubSite } from './shared';

// 30 days of visitors, peaking on the 27 Aug announcement (sample)
const DAYS = ['5 Aug', '6 Aug', '7 Aug', '8 Aug', '9 Aug', '10 Aug', '11 Aug', '12 Aug', '13 Aug', '14 Aug', '15 Aug', '16 Aug', '17 Aug', '18 Aug', '19 Aug', '20 Aug', '21 Aug', '22 Aug', '23 Aug', '24 Aug', '25 Aug', '26 Aug', '27 Aug', '28 Aug', '29 Aug', '30 Aug', '31 Aug', '1 Sep', '2 Sep', '3 Sep'];
const VISITORS = [32, 42, 38, 51, 47, 59, 70, 58, 52, 49, 62, 76, 66, 58, 54, 52, 61, 55, 52, 58, 82, 120, 152, 138, 96, 84, 78, 74, 70, 68];
const PEAK = VISITORS.indexOf(Math.max(...VISITORS));

const SOURCES = [['40%', 'Direct / bookmark'], ['25%', 'ASX / email links'], ['17%', 'Google search'], ['12%', 'Social posts'], ['6%', 'Other websites']];

const PAGES: { path: string; widget: string; views: string; time: string; trend: number }[] = [
    { path: '/investors/investors-dashboard/', widget: 'Capital structure · Share price', views: '2,140', time: '1m 48s', trend: 31 },
    { path: '/investors/asx-announcements/', widget: 'Announcements', views: '1,620', time: '2m 05s', trend: 18 },
    { path: '/about-us/board-management/', widget: 'Board & management', views: '740', time: '1m 12s', trend: 4 },
    { path: '/news-media/', widget: 'News & media', views: '610', time: '0m 58s', trend: -3 },
    { path: '/contact-us/', widget: 'Contact form', views: '420', time: '1m 30s', trend: 9 },
];

const ACTIVITY: { icon: React.ReactNode; text: string; when: string }[] = [
    { icon: <FiMail />, text: 'Margaret Chua sent a message via Contact us', when: '2 hours ago' },
    { icon: <FiUsers />, text: 'Brendan Taylor signed up on the website', when: '3 hours ago' },
    { icon: <FiFileText />, text: 'News article “Gold Mountain assay results” published', when: 'Yesterday · by Wallace Turner' },
    { icon: <FiMessageCircle />, text: 'Answered: “Will there be another SPP this year?”', when: 'Yesterday · by Wallace Turner' },
    { icon: <FiFileText />, text: 'Board Charter updated in Corporate governance', when: '30 Jun · by Wallace Turner' },
];

// Website Hub › Overview (spec 271:8702)
const Overview: React.FC = () => {
    const history = useHistory();
    const { host, url } = useHubSite();
    const missing = WIDGETS.filter(w => !w.installed);
    const missingNames = missing.slice(0, 2).map(w => w.name.toLowerCase()).join(', ');

    const chart = {
        animation: false,
        grid: { left: 36, right: 12, top: 36, bottom: 28 },
        xAxis: { type: 'category', data: DAYS, boundaryGap: false, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#94a3b8', fontSize: 10, interval: 6 } },
        yAxis: { type: 'value', min: 0, max: 150, interval: 50, splitLine: { lineStyle: { color: '#eef1f6' } }, axisLabel: { color: '#94a3b8', fontSize: 10 } },
        tooltip: { trigger: 'axis', backgroundColor: '#0b1739', borderWidth: 0, textStyle: { color: '#fff', fontSize: 11 }, formatter: (p: { name: string; value: number }[]) => `${p[0].name} · ${p[0].value} visitors` },
        series: [{
            type: 'line', data: VISITORS, smooth: false, symbol: 'none', lineStyle: { color: '#00B5D1', width: 2 },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(0,181,209,0.35)' }, { offset: 1, color: 'rgba(0,181,209,0.02)' }] } },
            markPoint: { symbol: 'pin', symbolSize: 0, data: [{ coord: [PEAK, VISITORS[PEAK]], value: '27 Aug · drilling update' }], label: { show: true, position: 'top', offset: [0, -6], color: '#0093ab', fontSize: 10, fontWeight: 700, backgroundColor: '#e0f7fb', padding: [3, 8], borderRadius: 999, formatter: '{c}' } },
        }],
    };

    return <>
        <HubHeader title="Website Hub" sub={`${host} · your WordPress website with Investi widgets embedded`}>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/list')}><FiSliders /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url()} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View Website</a>
        </HubHeader>

        <div className="hub-stats">
            <StatCard icon={<FiTrendingUp />} label="Visitors (30 days)" value="1,860" sub="+22% vs previous 30 days" badge={<span className="badge ok">+22%</span>} />
            <StatCard icon={<FiGlobe />} label="Page views (30 days)" value="6,420" sub="3.4 pages per visit" />
            <StatCard icon={<FiUsers />} label="New sign‑ups (30 days)" value="38" sub="1,210 with an account" />
            <StatCard icon={<FiSliders />} label="Widgets on your website" value={`${WIDGETS.length - missing.length} of ${WIDGETS.length}`} sub={`${missing.length} widgets are not on the website yet`} />
        </div>

        <div className="cs-row">
            <div className="cs-main">
                <section className="card" data-tour="hub-overview-attention">
                    <div className="section-title">
                        <span className="icon-tile"><FiAlertCircle /></span>
                        <div className="text"><h2>Needs your attention</h2><div className="hint">3 things to look at on the Website Hub</div></div>
                    </div>
                    <div className="hub-attention">
                        <div><div className="t"><strong>2 investor questions have waited more than 48 hours</strong><span>Dev Patel (cash position) and Sarah O’Neill (TSX listing)</span></div>
                            <button type="button" className="btn-outline" onClick={() => history.push('/communications/investor-questions')}>Answer Now</button></div>
                        <div><div className="t"><strong>{NEW_MESSAGES} New Messages from the Contact Us Form</strong><span>Latest: “Request for site visit” from Margaret Chua, 2 hours ago</span></div>
                            <button type="button" className="btn-outline" onClick={() => history.push(hubPath('contact-forms'))}>Read Messages</button></div>
                        {missing.length > 0 && <div><div className="t"><strong>{missing.length} widgets still to add — {missingNames}</strong><span>Install them from Client settings › Widgets</span></div>
                            <button type="button" className="btn-outline" onClick={() => history.push('/client-settings/widgets/list')}>Open Client Settings</button></div>}
                    </div>
                </section>

                <section className="card" data-tour="hub-overview-traffic">
                    <div className="section-title">
                        <span className="icon-tile"><FiBarChart2 /></span>
                        <div className="text"><h2>Website visitors over the last 30 days</h2><div className="hint">Peaks follow ASX announcements — 27 Aug was the Gold Mountain drilling update</div></div>
                        <button type="button" className="link in-title" onClick={() => history.push('/dashboard/analytics')}>Full Analytics <FiArrowRight /></button>
                    </div>
                    <ReactECharts option={chart} style={{ height: 180 }} />
                    <div className="hub-sources">
                        {SOURCES.map(([v, l]) => <div key={l}><strong>{v}</strong><span>{l}</span></div>)}
                    </div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiFileText /></span>
                        <div className="text"><h2>Most visited pages</h2><div className="hint">Pages on {host} that use an Investi widget</div></div>
                    </div>
                    <div className="hub-table">
                        <div className="tr th"><span className="page">Page</span><span className="widget">Investi widget</span><span className="num">Views</span><span className="num">Avg. time</span><span className="num">Trend</span></div>
                        {PAGES.map(p => <div key={p.path} className="tr">
                            <span className="page"><strong>{p.path}</strong></span>
                            <span className="widget">{p.widget}</span>
                            <span className="num">{p.views}</span>
                            <span className="num">{p.time}</span>
                            <span className={`num trend ${p.trend < 0 ? 'down' : 'up'}`}>{p.trend > 0 ? '+' : ''}{p.trend}%</span>
                        </div>)}
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiGlobe /></span>
                        <div className="text"><h2>What's on the website</h2><div className="hint">Content Investi manages for you</div></div>
                    </div>
                    <div className="hub-manage-list">
                        {CONTENT_SECTIONS.map(s => <div key={s.slug}>
                            <div className="t"><strong>{s.overviewTitle ?? s.title}</strong><span>{s.overviewLine}</span></div>
                            <button type="button" className="link" onClick={() => history.push(hubPath('content', s.slug))}>Manage <FiArrowRight /></button>
                        </div>)}
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><div className="text"><h2>Recent activity</h2></div></div>
                    <div className="hub-activity" tabIndex={0} role="region" aria-label="Recent activity">
                        {ACTIVITY.map((a, i) => <div key={i}>{a.icon}<div className="t"><strong>{a.text}</strong><span>{a.when}</span></div></div>)}
                    </div>
                </section>
            </div>
        </div>
    </>;
};

export default Overview;
